import { useMemo, useState } from 'react';
import { GUEST_DAY_OPTIONS, RoomRole, type GuestDays, type RoomMemberSummary } from 'minka-shared';
import { Button, Dropdown, DropdownItem, Modal } from '@/components/common';
import { PlayerAvatar } from '@/components/common/PlayerAvatar';
import { useRoomMembers } from '@/hooks/useRoomMembers';
import { formatGuestExpiry, isGuestAccessExpired } from '@/utils/guest-access';
import { countOf } from '@/utils/plural';
import {
  FaClock,
  FaCrown,
  FaExclamationTriangle,
  FaUser,
  FaUserShield,
  FaUserTimes
} from 'react-icons/fa';

const LAST_ADMIN_TOOLTIP = 'An island needs at least one active admin';
const DEACTIVATED_TOOLTIP = 'This account is deactivated';

interface MemberListProps {
  roomId: string;
  /**
   * `admin` is the in-game island administration panel, `superadmin` the
   * console. The actions are the same — the server draws the line — but the
   * console speaks about "this island" rather than "your island".
   */
  mode: 'admin' | 'superadmin';
  /** The viewer, whose own row never offers to promote or demote them. */
  currentUserId?: string;
}

/** Admin · Resident · Guest, with the guest's window spelled out. */
function RoleBadge({ member }: { member: RoomMemberSummary }) {
  if (member.role === RoomRole.ADMIN) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-semibold uppercase whitespace-nowrap rounded-full bg-gradient-to-r from-game-orange/20 to-game-red/20 border border-game-orange/30 text-game-accent">
        <FaCrown className="text-game-golden text-xs flex-shrink-0" />
        Admin
      </span>
    );
  }

  if (member.role === RoomRole.PLAYER) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-semibold uppercase whitespace-nowrap rounded-full bg-game-medium/30 border border-game-medium/50 text-gray-300">
        <FaUser className="text-xs flex-shrink-0" />
        Resident
      </span>
    );
  }

  const expired = isGuestAccessExpired(member.expiresAt);

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-semibold uppercase whitespace-nowrap rounded-full border ${
        expired
          ? 'bg-game-orange/20 border-game-orange/40 text-game-accent'
          : 'bg-game-medium/30 border-game-medium/50 text-gray-300'
      }`}
    >
      <FaClock className="text-xs flex-shrink-0" />
      {expired
        ? 'Guest · Expired'
        : `Guest${member.expiresAt ? ` until ${formatGuestExpiry(member.expiresAt)}` : ''}`}
    </span>
  );
}

/**
 * The island's members with the four actions of §6.4: change a member's role,
 * extend a guest's window, remove. Every role action names the role it hands
 * out ("Make admin", "Make resident") rather than a direction (#66).
 *
 * The last active admin is the one row the client reasons about on its own: the
 * server refuses to strand an island (409 `last_admin`), and the list disables
 * the two controls that would try, so the rule is visible before it is hit
 * rather than only as an error afterwards. Everything else is left to the
 * server — the buttons offer what the role allows and report what comes back.
 */
export function MemberList({ roomId, mode, currentUserId }: MemberListProps) {
  const { members, isLoading, error, pendingUserId, changeRole, extendGuest, removeMember } =
    useRoomMembers(roomId);
  const [memberToRemove, setMemberToRemove] = useState<RoomMemberSummary | null>(null);

  // Deactivated admins protect nothing (§3 active permission), so the guard is
  // about the *active* ones: exactly one of them means that one is untouchable.
  const lastActiveAdminId = useMemo(() => {
    const activeAdmins = members.filter(
      member => member.role === RoomRole.ADMIN && member.isActive
    );
    return activeAdmins.length === 1 ? activeAdmins[0].userId : null;
  }, [members]);

  const handleRemoveConfirmed = async () => {
    if (!memberToRemove) return;

    try {
      await removeMember(memberToRemove.userId);
      setMemberToRemove(null);
    } catch {
      // The hook has already put the refusal in `error`; keep the dialog open.
    }
  };

  if (isLoading && members.length === 0) {
    return <p className="text-gray-400 italic text-center py-5">Loading members…</p>;
  }

  return (
    <div className="space-y-3">
      {error && (
        <div className="p-3 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-lg">
          <div className="flex items-center gap-2">
            <FaExclamationTriangle className="text-game-golden flex-shrink-0 text-xs" />
            <span className="font-medium text-sm">{error}</span>
          </div>
        </div>
      )}

      {members.length === 0 ? (
        <p className="text-gray-400 italic text-center py-5">
          {mode === 'superadmin'
            ? 'This island has no members.'
            : 'Nobody has joined this island yet. Invite someone to get started.'}
        </p>
      ) : (
        <ul className="space-y-2 max-h-80 overflow-y-auto">
          {members.map(member => {
            const isSelf = member.userId === currentUserId;
            const isLastActiveAdmin = member.userId === lastActiveAdminId;
            const isPending = pendingUserId === member.userId;
            const guardTitle = isLastActiveAdmin ? LAST_ADMIN_TOOLTIP : undefined;
            // A deactivated account cannot log in, so promoting it or buying it
            // more guest days changes nothing a person could use. Removing it
            // still makes sense — that is how an island is tidied up.
            const canChangeStanding = member.isActive && !isPending;

            return (
              <li
                key={member.userId}
                className={`flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg border ${
                  member.isActive
                    ? 'bg-game-dark/30 border-game-medium/20'
                    : 'bg-game-dark/10 border-game-medium/10 opacity-50'
                }`}
              >
                {/* `basis-64` rather than a bare `flex-1`: with a 0% basis the
                    identity block never asks for enough width to push the
                    actions onto their own line, so between ~470 and ~630px it
                    was squeezed to a sliver and the badge painted over the
                    buttons (#63). */}
                <div className="flex items-center gap-3 min-w-0 flex-1 basis-64">
                  {/* The avatar is a circle drawn at a fixed size: as a flex
                      child it would otherwise give up width to the badges and
                      render as an ellipse (#63). */}
                  <PlayerAvatar
                    username={member.username}
                    avatar={member.avatar}
                    size="sm"
                    className="flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* `max-w-full` is load-bearing: without it the name is a
                          flex item sized to its content, so `truncate` never
                          gets a box narrow enough to ellipsize. */}
                      <span className="text-white font-medium truncate max-w-full">
                        {member.username}
                        {isSelf && <span className="text-game-golden ml-1">(You)</span>}
                      </span>
                      <RoleBadge member={member} />
                      {!member.isActive && (
                        <span className="px-2 py-0.5 text-xs font-semibold uppercase whitespace-nowrap rounded-full bg-game-medium/20 border border-game-medium/40 text-gray-400">
                          Deactivated
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2">
                  {!isSelf && member.role === RoomRole.GUEST && (
                    <Button
                      variant="secondary"
                      onClick={() => void changeRole(member.userId, RoomRole.PLAYER)}
                      disabled={!canChangeStanding}
                      title={member.isActive ? 'Make this guest a permanent resident' : DEACTIVATED_TOOLTIP}
                      aria-label={`Make ${member.username} resident`}
                      className="px-3 py-1 text-sm whitespace-nowrap"
                    >
                      <FaUser className="text-xs flex-shrink-0" />
                      Make resident
                    </Button>
                  )}

                  {!isSelf && member.role !== RoomRole.ADMIN && (
                    <Button
                      variant="secondary"
                      onClick={() => void changeRole(member.userId, RoomRole.ADMIN)}
                      disabled={!canChangeStanding}
                      title={member.isActive ? 'Give this member the island admin role' : DEACTIVATED_TOOLTIP}
                      aria-label={`Make ${member.username} admin`}
                      className="px-3 py-1 text-sm whitespace-nowrap"
                    >
                      <FaUserShield className="text-xs flex-shrink-0" />
                      Make admin
                    </Button>
                  )}

                  {!isSelf && member.role === RoomRole.ADMIN && (
                    <Button
                      variant="secondary"
                      onClick={() => void changeRole(member.userId, RoomRole.PLAYER)}
                      disabled={isPending || isLastActiveAdmin}
                      title={guardTitle ?? 'Take the admin role back to resident'}
                      aria-label={`Make ${member.username} resident`}
                      className="px-3 py-1 text-sm whitespace-nowrap"
                    >
                      <FaUser className="text-xs flex-shrink-0" />
                      Make resident
                    </Button>
                  )}

                  {member.role === RoomRole.GUEST && (
                    <Dropdown
                      position="below"
                      align="right"
                      minWidth="10rem"
                      trigger={({ toggle }) => (
                        <Button
                          variant="secondary"
                          onClick={toggle}
                          disabled={!canChangeStanding}
                          title={member.isActive ? 'Extend guest access' : DEACTIVATED_TOOLTIP}
                          aria-label={`Extend ${member.username}'s guest access`}
                          className="px-3 py-1 text-sm whitespace-nowrap"
                        >
                          <FaClock className="text-xs flex-shrink-0" />
                          Extend
                        </Button>
                      )}
                    >
                      {GUEST_DAY_OPTIONS.map(days => (
                        <DropdownItem
                          key={days}
                          label={countOf(days, 'day')}
                          onClick={() => void extendGuest(member.userId, days as GuestDays)}
                        />
                      ))}
                    </Dropdown>
                  )}

                  {/* Removing yourself is "leave", and that lives on the
                      island list, where you end up afterwards anyway. */}
                  {!isSelf && (
                    <Button
                      variant="danger"
                      onClick={() => setMemberToRemove(member)}
                      disabled={isPending || isLastActiveAdmin}
                      title={guardTitle ?? 'Remove from this island'}
                      aria-label={`Remove ${member.username} from this island`}
                      className="px-3 py-1 text-sm whitespace-nowrap"
                    >
                      <FaUserTimes className="text-xs flex-shrink-0" />
                      Remove
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Modal
        isOpen={memberToRemove !== null}
        onClose={() => setMemberToRemove(null)}
        title="Remove member"
        size="md"
        className="border-game-orange"
      >
        <div className="space-y-6">
          <div className="p-4 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-lg">
            <div className="flex items-start gap-3">
              <FaExclamationTriangle className="text-game-golden flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium mb-2">
                  Remove <strong className="text-white">{memberToRemove?.username}</strong> from this
                  island?
                </p>
                <p className="text-sm">
                  They lose access immediately. An admin can invite them back at any time.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setMemberToRemove(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => void handleRemoveConfirmed()}
              isLoading={pendingUserId === memberToRemove?.userId}
            >
              Remove
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
